import { logEvent, LogLevel } from '@praxis/component-logging'
import _, { isEmpty } from 'lodash'
import { getLocalTimezone } from 'mvscommoncomponents'
import {
  CTP_PSQA_OFFICE,
  CTP_SOURCING_OFFICE,
  CTV_SOURCING_OFFICE,
  MANUFACTURING_TYPE_ID,
  MANUFACTURING_TYPE_ID_FABRIC,
  MANUFACTURING_TYPE_ID_FINISHED_GOOD,
  MANUFACTURING_TYPE_ID_PACKAGING,
  MANUFACTURING_TYPE_ID_RAWMATERIAL,
  MANUFACTURING_TYPE_ID_SECONDARY_PROCESSING,
  MANUFACTURING_TYPE_ID_TRIM,
  PAGE_NUMBER,
  PAGE_SIZE,
  RFI3_STATUS_CODE,
  RFI_TYPE,
  RFI_TYPE_LOCATION,
  RFI_TYPE_QUESTIONNAIRE,
  RFI_TYPE_SUPPLIER,
  SEARCH_QUERY,
  SORT_ASCENDING,
  SORT_BY,
  SORT_DIRECTION,
  SUPPLIER_TYPE,
} from '../common/commonConstants'
import appConfig from '../config/appConfig'
import {
  handleDelete,
  handleGet,
  handlePost,
  handlePut,
  logErrorApiCalls,
} from '../utils/httpUtils'

/**
 * Get the left side menu options
 * Menu includes all RFI1 & RFI2 details of the supplier
 * @param {*} supplierId
 * @returns
 */
export async function getRfiMenuOptions(supplierId, isReadonly) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.getRFILeftMenu}`
  url = url.replace('SUPPLIERID', supplierId)

  if (isReadonly) {
    url = url + `&read_only=${true}`
  }

  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Get master data
 * @param {*} rfiType
 * @param {*} manufacturingTypeId
 * @returns
 */
export async function getMasterData(
  rfiType,
  manufacturingTypeId = MANUFACTURING_TYPE_ID_FINISHED_GOOD,
) {
  const config = await appConfig()
  let moduleType =
    rfiType.toLowerCase() === RFI_TYPE_SUPPLIER
      ? config.mfelcm.services.getSupplierMasterData
      : config.mfelcm.services.getLocationMasterData
  if (
    rfiType.toLowerCase() === RFI_TYPE_QUESTIONNAIRE &&
    manufacturingTypeId === MANUFACTURING_TYPE_ID_PACKAGING
  ) {
    moduleType = moduleType + '&module=rfiQuestionnaire&module=packaging'
  } else {
    if (manufacturingTypeId === MANUFACTURING_TYPE_ID_FABRIC) {
      moduleType = moduleType + '&module=fabric'
    } else if (manufacturingTypeId === MANUFACTURING_TYPE_ID_TRIM) {
      moduleType = moduleType + '&module=trim'
    } else if (manufacturingTypeId === MANUFACTURING_TYPE_ID_PACKAGING) {
      moduleType = moduleType + '&module=packaging'
    } else if (manufacturingTypeId === MANUFACTURING_TYPE_ID_RAWMATERIAL) {
      moduleType = moduleType + '&module=rawMaterial'
    } else if (
      manufacturingTypeId === MANUFACTURING_TYPE_ID_SECONDARY_PROCESSING
    ) {
      moduleType = moduleType + '&module=secondaryProcessing'
    }
  }

  let url = `${config.mfelcm.rfiExternalAppServiceUrl}/${moduleType}`

  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Get dynamic master data
 * @returns
 */
export async function getDynamicMasterData() {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.getDynamicMasterData}`

  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

//#region Chat

/**
 * Get list of all active users of AD group defined in allUsersADGroup of config.js
 */
export async function getAllActiveUsers() {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.fetchGroupUsersList}`

  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Get RFI Approval comments for External/Internal Users
 * @param {*} rfiType
 * @param {*} supplierId
 * @param {*} locationId
 * @param {*} view
 * @param {*} manufacturingTypeId
 * @returns
 */
export async function getRFIApprovalComments(
  rfiType,
  supplierId,
  locationId,
  view,
  manufacturingTypeId,
) {
  const config = await appConfig()
  let url =
    view.toLowerCase() === RFI_TYPE_SUPPLIER
      ? `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.getRFIExternalApprovalComments}`
      : `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.getRFIApprovalComments}`

  if (rfiType.toLowerCase() === RFI_TYPE_LOCATION) {
    url = url + `&location_id=${locationId}`
  }
  url = url
    .replace('RFI_TYPE', rfiType)
    .replace('SUPPLIER_ID', supplierId)
    .replace('MANUFACTURING_TYPE_ID', manufacturingTypeId)

  if (view.toLowerCase() === RFI_TYPE_SUPPLIER) {
    url = url.replace('USERTYPE', RFI_TYPE_SUPPLIER)
  }

  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Submit RFI comment for External/Internal Users
 * @param {*} data
 * @param {*} view
 * @param {*} userType
 * @returns
 */
export async function submitRFIApprovalComments(data, view, userType) {
  const config = await appConfig()
  let url

  view.toLowerCase() === RFI_TYPE_SUPPLIER
    ? (url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.submitRFIExternalComments}`)
    : (url = `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.submitRFIComments}`)

  url = url.replace('USERTYPE', userType)

  return new Promise((resolve, reject) => {
    handlePut(url, null, null, data)
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

//#endregion

//#region EMail Popup

/**
 * Get Rfi email list
 * @param {*} rfiType
 * @param {*} supplierId
 * @param {*} manufacturingTypeId
 * @param {*} locationId
 * @returns
 */
export async function getRfiEmailList(
  rfiType,
  supplierId,
  manufacturingTypeId,
  locationId = null,
) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.getSaveEmailList}`
  url = url
    .replace('RFITYPE', rfiType.toLowerCase())
    .replace('SUPPLIERID', supplierId)
    .replace('MANUFACTURING_TYPE_ID', manufacturingTypeId)
  if (locationId) {
    url = `${url}&location_id=${locationId}`
  }

  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        if (!response.data) {
          reject('No data returned')
        }
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Save email list
 * @param {*} emailDataPayload
 * @param {*} rfiType
 * @param {*} supplierId
 * @param {*} manufacturingTypeId
 * @param {*} locationId
 * @returns
 */
export async function saveRfiEmailList(
  emailDataPayload,
  rfiType,
  supplierId,
  manufacturingTypeId,
  locationId = null,
) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.getSaveEmailList}`
  url = url
    .replace('RFITYPE', rfiType)
    .replace('SUPPLIERID', supplierId)
    .replace('MANUFACTURING_TYPE_ID', manufacturingTypeId)
  if (locationId) {
    url = `${url}&location_id=${locationId}`
  }

  let multipartHeaderExtra = [
    {
      name: 'headers.content-type',
      value: 'application/json',
    },
  ]
  return new Promise((resolve, reject) => {
    handlePut(url, null, multipartHeaderExtra, emailDataPayload)
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

//#endregion

/**
 * Get RFI template
 * @param {*} supplierId
 * @param {*} rfiType
 * @param {*} manufacturingTypeId
 * @param {*} locationId
 * @returns
 */
export async function getRfiTemplate(
  supplierId,
  rfiType,
  manufacturingTypeId,
  locationId = null,
  isReadonly,
) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.getRfiDataTemplate}`

  if (
    [RFI_TYPE_LOCATION, RFI_TYPE_QUESTIONNAIRE].includes(rfiType.toLowerCase())
  ) {
    url = url + `&location_id=${locationId}`
  }
  if (isReadonly) {
    url = url + `&read_only=${true}`
  }
  url = url
    .replace('RFITYPE', rfiType)
    .replace('SUPPLIERID', supplierId)
    .replace('MANUFACTURING_TYPE_ID', manufacturingTypeId)

  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * GET - real RFI1 data
 * @param {*} supplierId
 * @param {*} rfiType
 * @param {*} manufacturingTypeId
 * @param {*} locationId
 * @param {*} isReadonly
 * @returns
 */
export async function getRfiData(
  supplierId,
  rfiType,
  manufacturingTypeId,
  locationId = null,
  isReadonly,
) {
  const config = await appConfig()
  let url = ''

  rfiType.toLowerCase() === RFI_TYPE_SUPPLIER
    ? (url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.getRfi1Data}`)
    : (url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.getRfi2Data}`)

  if (rfiType.toLowerCase() === RFI_TYPE_LOCATION) {
    url = url.replace('LOCATIONID', locationId)
  }
  url = url
    .replace('SUPPLIERID', supplierId)
    .replace('MANUFACTURING_TYPE_ID', manufacturingTypeId)

  if (isReadonly) {
    url = url + `&read_only=${true}`
  }

  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Save RFI Data
 * @param {*} payload
 * @param {*} rfiType
 * @param {*} isLogError
 * @returns
 */
export async function saveRfiData(payload, rfiType, isLogError = false) {
  const config = await appConfig()
  let url = ''

  rfiType.toLowerCase() === RFI_TYPE_SUPPLIER
    ? (url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.saveRfi1Data}`)
    : (url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.saveRfi2Data}`)

  let multipartHeaderExtra = [
    {
      name: 'headers.content-type',
      value: 'application/json',
    },
  ]

  return new Promise((resolve, reject) => {
    handlePut(url, null, multipartHeaderExtra, payload)
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        if (isLogError) {
          let errorPayloadToLog = {
            supplierId: payload?.supplier_id,
            locationId: payload?.location_id,
            requestPayload: payload,
            url: url,
            timezone: getLocalTimezone(),
            errorStatus: error.response.status,
            errorResponseData: error.response.data,
          }
          logEvent({ message: errorPayloadToLog }, { level: LogLevel.Error })
        }
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Submit RFI Data
 * @param {*} payload
 * @param {*} isSubmit
 * @param {*} isLogError
 * @param {*} rfiType
 * @returns
 */
export async function submitRfiData(
  payload,
  isSubmit = false,
  isLogError = false,
  rfiType,
) {
  const config = await appConfig()
  let url = ''

  rfiType.toLowerCase() === RFI_TYPE_SUPPLIER
    ? (url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.submitRfiData}`)
    : (url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.submitRfi2Data}`)

  url = url.concat(`?is_submit=`).concat(isSubmit)

  let multipartHeaderExtra = [
    {
      name: 'headers.content-type',
      value: 'application/json',
    },
  ]

  return new Promise((resolve, reject) => {
    handlePut(url, null, multipartHeaderExtra, payload)
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        if (isLogError) {
          let errorPayloadToLog = {
            supplierId: payload?.supplier_id,
            requestPayload: payload,
            url: url,
            timezone: getLocalTimezone(),
            errorStatus: error.response?.status,
            errorResponseData: error.response?.data,
          }
          logEvent({ message: errorPayloadToLog }, { level: LogLevel.Error })
        }
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Post RFI action (Approved/Rejected/SentBack)
 * @param {*} rfiActionPayload
 */
export async function postRfiAction(rfiActionPayload) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.submitVetting}`
  return new Promise((resolve, reject) => {
    handlePut(url, null, null, rfiActionPayload)
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Save product capabilities and comments for Tier 2 approvals (from product capability selection popup)
 * @param {*} rfiActionPayload
 */
export async function saveCapabilitiesAndComments(rfiActionPayload) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.saveCapabilities}`
  return new Promise((resolve, reject) => {
    handlePut(url, null, null, rfiActionPayload)
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Get RFI Audit Trails
 * @param {*} rfiType
 * @param {*} supplierId
 * @param {*} locationId
 * @param {*} manufacturingTypeId
 * @returns
 */
export async function getRFIAuditTrails(
  rfiType,
  supplierId,
  locationId,
  manufacturingTypeId,
) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.getRFIAuditTrails}`
  url = url
    .replace('RFI_TYPE', rfiType)
    .replace('SUPPLIER_ID', supplierId)
    .replace('MANUFACTURING_TYPE_ID', manufacturingTypeId)
  if (rfiType.toLowerCase() === RFI_TYPE_LOCATION) {
    url = url + `&location_id=${locationId}`
  }
  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

//#region Certification

/**
 * Add certification row
 * @param {*} rfiType
 * @param {*} supplierId
 * @param {*} locationId
 * @param {*} componentName
 * @param {*} certificationPayload
 * @param {*} manufacturingTypeId
 * @param {*} isLogError
 * @returns
 */
export async function addCertificationComponentRow(
  rfiType,
  supplierId,
  locationId,
  componentName,
  certificationPayload,
  manufacturingTypeId,
  isLogError = false,
) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.addSpecificRowComponent}`
  url = url
    .replace('RFITYPE', rfiType)
    .replace('SUPPLIERID', supplierId)
    .replace('LOCATIONID', locationId ? locationId : '')
    .replace('COMPONENTNAME', componentName)
    .replace('MANUFACTURING_TYPE_ID', manufacturingTypeId)
  return new Promise((resolve, reject) => {
    handlePost(url, null, null, certificationPayload)
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        if (isLogError) {
          let errorPayloadToLog = {
            supplierId: supplierId,
            locationId: locationId,
            componentName: componentName,
            requestPayload: certificationPayload,
            url: url,
            timezone: getLocalTimezone(),
            errorStatus: error.response.status,
            errorResponseData: error.response.data,
          }
          logEvent({ message: errorPayloadToLog }, { level: LogLevel.Error })
        }
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Delete certificate row
 * @param {*} rfiType
 * @param {*} supplierId
 * @param {*} locationId
 * @param {*} componentName
 * @param {*} rowIndex
 * @param {*} manufacturingTypeId
 * @returns
 */
export async function deleteSpecificComponentRow(
  rfiType,
  supplierId,
  locationId,
  componentName,
  rowIndex,
  manufacturingTypeId,
) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.deleteSpecificRowComponent}`
  url = url
    .replace('RFITYPE', rfiType)
    .replace('SUPPLIERID', supplierId)
    .replace('LOCATIONID', locationId ? locationId : '')
    .replace('COMPONENTNAME', componentName)
    .replace('ROWINDEX', rowIndex)
    .replace('MANUFACTURING_TYPE_ID', manufacturingTypeId)
  return new Promise((resolve, reject) => {
    handleDelete(url)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

//#endregion

//#region FileUploader

/**
 * Upload RFI File
 * @param {*} rfiType
 * @param {*} componentName
 * @param {*} isOverride
 * @param {*} files
 * @param {*} index
 * @param {*} supplierId
 * @param {*} locationId
 * @param {*} manufacturingTypeId
 * @returns
 */
export async function uploadFiles(
  rfiType,
  componentName,
  isOverride,
  files,
  index = -1,
  supplierId,
  locationId,
  manufacturingTypeId,
) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.uploadRfiFiles}`
  url = url
    .replace('RFITYPE', rfiType)
    .replace('COMPONENTNAME', componentName)
    .replace('IS_OVERRIDE', isOverride)
    .replace('INDEX', index)
    .replace('SUPPLIER_ID', supplierId)
    .replace('MANUFACTURING_TYPE_ID', manufacturingTypeId)

  if (
    rfiType.toLowerCase() === RFI_TYPE_LOCATION ||
    rfiType.toLowerCase() === RFI_TYPE_QUESTIONNAIRE
  ) {
    url = url.concat('&location_id=', locationId)
  }

  let multipartHeaderExtra = [
    {
      name: 'headers.content-type',
      value: 'multipart/form-data',
    },
  ]

  let formData = new FormData()
  Array.from(files).forEach((file) => {
    formData.append('files', file)
  })
  return new Promise((resolve, reject) => {
    handlePut(url, null, multipartHeaderExtra, formData)
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        reject(error.response.data)
      })
  })
}

/**
 * Download file
 * @param {*} rfiType
 * @param {*} componentName
 * @param {*} fileName
 * @param {*} tossKey
 * @param {*} index
 * @returns
 */
export async function downloadFile(
  rfiType,
  componentName,
  fileName,
  tossKey,
  index,
) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.downloadRfiFile}`
  url = url
    .replace('RFITYPE', rfiType)
    .replace('COMPONENTNAME', componentName)
    .replace('FILENAME', encodeURIComponent(fileName))
    .replace('TOSS_KEY', encodeURIComponent(tossKey))
    .replace('INDEX', index)
  return handleGet(url, null, 'blob')
}

/**
 * Delete file
 * @param {*} rfiType
 * @param {*} id
 * @param {*} componentName
 * @param {*} fileName
 * @param {*} index
 * @param {*} supplierId
 * @param {*} locationId
 * @param {*} manufacturingTypeId
 * @returns
 */
export async function deleteFile(
  rfiType,
  componentName,
  fileName,
  index,
  supplierId,
  locationId,
  manufacturingTypeId,
) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.deleteRfiFile}`
  url = url
    .replace('RFITYPE', rfiType)
    .replace('COMPONENTNAME', componentName)
    .replace('FILENAME', encodeURIComponent(fileName))
    .replace('INDEX', index)
    .replace('SUPPLIER_ID', supplierId)
    .replace('MANUFACTURING_TYPE_ID', manufacturingTypeId)

  if (
    rfiType.toLowerCase() === RFI_TYPE_LOCATION ||
    rfiType.toLowerCase() === RFI_TYPE_QUESTIONNAIRE
  ) {
    url = url.concat('&location_id=', locationId)
  }

  return new Promise((resolve, reject) => {
    handleDelete(url)
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        reject(error.response.data)
      })
  })
}

//#endregion

/**
 * Get Vetting Summary Filters
 * @param {*} rfiType
 * @returns
 */
export async function getFiltersForVettingSummary(rfiType) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.getRFIFilters}`
  url = url.replace(RFI_TYPE, rfiType)
  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Get RFI Approval Summary list
 * @param {*} scope
 * @returns
 */
export async function getRFIApprovalList(
  rfiTypes,
  pageNumber,
  pageSize,
  sortDirection,
  sortBy,
  searchQuery,
  manufacturingType,
  rfi1Status,
  rfi2Status,
  rfi3Status,
  supplierType,
  ctvSourcingOffice,
  ctpSourcingOffice,
  ctpPsqaOffice,
  rfiLastModifiedDate,
) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.getRFIAuditSummaryList}`

  if (rfiTypes) {
    rfiTypes.forEach((eachRfiType, index) => {
      url = url.concat('&rfi_type=').concat(_.capitalize(eachRfiType))
    })
  }

  if (rfiTypes && rfi2Status && rfiTypes.includes(RFI_TYPE_LOCATION)) {
    url = url.concat('&status_code=').concat(rfi2Status)
  } else if (rfiTypes && rfi1Status && rfiTypes.includes(RFI_TYPE_SUPPLIER)) {
    url = url.concat('&status_code=').concat(rfi1Status)
  }
  if (rfi3Status) {
    url = url.concat('&rfi3_status_code=').concat(rfi3Status)
  }

  if (
    rfiLastModifiedDate &&
    !isEmpty(rfiLastModifiedDate.startDate) &&
    !isEmpty(rfiLastModifiedDate.endDate)
  ) {
    url = url
      .concat('&rfi_last_modified_start_date=')
      .concat(rfiLastModifiedDate.startDate)
      .concat('&rfi_last_modified_end_date=')
      .concat(rfiLastModifiedDate.endDate)
  }

  url = url
    .replace(PAGE_NUMBER, pageNumber)
    .replace(PAGE_SIZE, pageSize)
    .replace(SORT_DIRECTION, sortDirection)
    .replace(SORT_BY, sortBy)
    .replace(SEARCH_QUERY, searchQuery)
    .replace(SUPPLIER_TYPE, supplierType)
    .replace(MANUFACTURING_TYPE_ID, manufacturingType)
    .replace(RFI3_STATUS_CODE, rfi3Status)
    .replace(SUPPLIER_TYPE, supplierType)
    .replace(MANUFACTURING_TYPE_ID, manufacturingType)
    .replace(RFI3_STATUS_CODE, rfi3Status)
    .replace(SUPPLIER_TYPE, supplierType)
    .replace(CTV_SOURCING_OFFICE, ctvSourcingOffice)
    .replace(CTP_SOURCING_OFFICE, ctpSourcingOffice)
    .replace(CTP_PSQA_OFFICE, ctpPsqaOffice)

  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

export async function exportRFIApprovalExcel(
  rfiTypes,
  sortDirection,
  sortBy,
  searchQuery,
  manufacturingType,
  rfi1Status,
  rfi2Status,
  rfi3Status,
  supplierType,
) {
  const config = await appConfig()

  const url = new URL(
    `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm?.services?.exportRFIAuditSummaryExcel}`,
  )

  if (rfiTypes && rfiTypes.length) {
    rfiTypes.forEach((t) =>
      url.searchParams.append('rfi_type', _.capitalize(t)),
    )
  }

  if (rfiTypes && rfi2Status && rfiTypes.includes(RFI_TYPE_LOCATION)) {
    url.searchParams.set('status_code', rfi2Status)
  } else if (rfiTypes && rfi1Status && rfiTypes.includes(RFI_TYPE_SUPPLIER)) {
    url.searchParams.set('status_code', rfi1Status)
  }
  if (rfi3Status) url.searchParams.set('rfi3_status_code', rfi3Status)

  url.searchParams.set(
    'sort_direction',
    sortDirection === SORT_ASCENDING ? 'asc' : 'desc',
  )
  if (sortBy) url.searchParams.set('sort_by', sortBy)
  if (searchQuery) url.searchParams.set('search_query', searchQuery)

  if (manufacturingType) {
    const csv = Array.isArray(manufacturingType)
      ? manufacturingType.join(',')
      : manufacturingType
    url.searchParams.set('manufacturing_type_id', csv)
  }
  if (supplierType) url.searchParams.set('supplier_type', supplierType)

  const res = await handleGet(url.toString(), {}, 'blob')

  const cd =
    res?.headers?.['content-disposition'] ||
    res?.headers?.get?.('Content-Disposition')
  let filename = 'vetting_summary.xlsx'
  if (cd) {
    const star = /filename\*\s*=\s*([^']*)''([^;]+)/i.exec(cd)
    const simple = /filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i.exec(cd)
    if (star && star[2]) {
      try {
        filename = decodeURIComponent(star[2])
      } catch {
        filename = star[2]
      }
    } else if (simple && (simple[1] || simple[2])) {
      filename = simple[1] || simple[2]
    }
  }

  const blob = res.data
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(href)
}

/**
 * Get RFI3 Data for supplier-location-manufacturingTypeId combination
 * @param {*} supplierId
 * @param {*} locationId
 * @param {*} manufacturingTypeId
 * @param {*} isReadOnly
 * @returns
 */
export async function getRfi3Data(
  supplierId,
  locationId,
  manufacturingTypeId,
  isReadOnly,
) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.getRFI3Data}`
  url = url
    .replace('SUPPLIER_ID', supplierId)
    .replace('LOCATION_ID', locationId)
    .replace('MANUFACTURING_TYPE_ID', manufacturingTypeId)
    .replace('READ_ONLY', isReadOnly)

  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Save RFI3 Data
 * @param {*} payload
 * @param {*} isLogError
 * @returns
 */
export async function saveRfi3Data(payload, isLogError = false) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.saveRFI3Data}`

  let multipartHeaderExtra = [
    {
      name: 'headers.content-type',
      value: 'application/json',
    },
  ]

  return new Promise((resolve, reject) => {
    handlePut(url, null, multipartHeaderExtra, payload)
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        if (isLogError) {
          let errorPayloadToLog = {
            supplierId: payload?.supplier_id,
            locationId: payload?.location_id,
            manufacturingTypeId: payload?.manufacturing_type_id,
            requestPayload: payload,
            url: url,
            timezone: getLocalTimezone(),
            errorStatus: error.response.status,
            errorResponseData: error.response.data,
          }
          logEvent({ message: errorPayloadToLog }, { level: LogLevel.Error })
        }
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Fast track RFI3
 * @param {*} payload
 * @returns
 */
export async function fastTrackRfi3(payload) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.fastTrackRfi3}`

  let multipartHeaderExtra = [
    {
      name: 'headers.content-type',
      value: 'application/json',
    },
  ]
  return new Promise((resolve, reject) => {
    handlePut(url, null, multipartHeaderExtra, payload)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Get permissions of a user by his AD groups
 * @param {*} memberAdGroupList
 */
export async function getUserPermissions(memberAdGroupList) {
  const config = await appConfig()
  let queryParam = 'adGroup=' + memberAdGroupList.join('&adGroup=')
  let url = `${config.mfelcm.adminControlUrl}/${config.mfelcm.services.adminControl.getPermissionByAdGroup}${queryParam}`
  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Get master data to fetch sourcing office names
 */
export async function getMasterDataLocationProfile() {
  const config = await appConfig()
  let url = `${config.mfelcm.locationProfileUrl}/${config.mfelcm.services.getMasterDataLocationProfile}`
  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

export async function getSelectedLocationDetails(locId, tabName) {
  const config = await appConfig()
  let url = `${config.mfelcm.locationProfileUrl}/${config.mfelcm.services.locationDetailsService}=${locId}&fieldGroup=${tabName}`
  return handleGet(url)
}

/**
 * Get CTV details
 * @param {*} ctvSourcingInformationPayload
 * @param {*} supplierId
 * @param {*} manufacturingTypeId
 */
export async function getCTVDetails(supplierId, manufacturingTypeId) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.ctvInformation}`

  url = url
    .replace('SUPPLIER_ID', supplierId)
    .replace('MANUFACTURING_TYPE_ID', manufacturingTypeId)

  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Save CTP Data
 * @param {*} locationId
 * @param {*} data
 * @param {*} locationName
 * @param {*} supplierId
 * @param {*} supplierName
 * @param {*} manufacturingType
 * @returns
 */
export async function saveCTPData(
  locationId,
  data,
  locationName,
  supplierId,
  supplierName,
  manufacturingType,
  address,
) {
  const config = await appConfig()
  let url = `${config.mfelcm.locationProfileUrl}/${config.mfelcm.services.saveCTPDetails}`
  url = url.replace('LOCATION_ID', locationId)
  url = url.replace('LOCATION_NAME', encodeURIComponent(locationName))
  url = url.replace('SUPPLIER_ID', supplierId)
  url = url.replace('SUPPLIER_NAME', encodeURIComponent(supplierName))
  url = url.replace('MANUFACTURING_TYPE_ID', manufacturingType)
  url = url.replace('ADDRESS', encodeURIComponent(address))

  let multipartHeaderExtra = [
    {
      name: 'headers.content-type',
      value: 'application/json',
    },
  ]
  return new Promise((resolve, reject) => {
    handlePut(url, null, multipartHeaderExtra, data)
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Save CTV details
 * @param {*} ctvSourcingInformationPayload
 * @param {*} supplierId
 * @param {*} supplierName
 * @param {*} manufacturingTypeId
 */
export async function saveCTVInformation(
  ctvSourcingInformationPayload,
  supplierId,
  supplierName,
  manufacturingTypeId,
) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.ctvInformation}`

  url = url
    .replace('SUPPLIER_ID', supplierId)
    .replace('SUPPLIER_NAME', supplierName)
    .replace('MANUFACTURING_TYPE_ID', manufacturingTypeId)

  return new Promise((resolve, reject) => {
    handlePut(url, null, null, ctvSourcingInformationPayload)
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Get All users list
 */
export async function getAllUsersList(searchKey) {
  const config = await appConfig()

  let url = `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.getAllUsers}`

  if (searchKey?.trim()) {
    url = `${url}?search_key=${encodeURIComponent(searchKey.trim())}`
  }

  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}
/**
 * Set Prefill popup to false
 * @param {*} rfiActionPayload
 */
export async function setRFI2PrefillPopup(rfiActionPayload) {
  const config = await appConfig()

  let url = `${config.mfelcm.rfiExternalAppServiceUrl}/${config.mfelcm.services.togglePrefillPopup}`
  return new Promise((resolve, reject) => {
    handlePut(url, null, null, rfiActionPayload)
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

export async function getUFLPAMatch(supplierId) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.getUFLPAMatch}`
  url = url.replace('SUPPLIER_ID', supplierId)
  return handleGet(url).then((response) => response.data)
}

/**
 * Get approved capabilities list for a supplier/location
 * Returns approved capabilities grouped by approval type (Target Designated, Vendor Sourced)
 * @param {string} rfiType - 'supplier' or 'location'
 * @param {string} supplierId - The supplier ID
 * @param {string} locationId - The location ID (for location type)
 * @param {number} manufacturingTypeId - The manufacturing type ID
 * @returns {Promise<Array>} Array of approved capability groups
 */
export async function getApprovedCapabilities(
  rfiType,
  supplierId,
  locationId,
  manufacturingTypeId,
) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.getApprovedCapabilities}`
  url = url
    .replace('RFI_TYPE', rfiType)
    .replace('SUPPLIER_ID', supplierId)
    .replace('LOCATION_ID', locationId || '')
    .replace('MANUFACTURING_TYPE_ID', manufacturingTypeId)

  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}

/**
 * Get Capability Audit Trails
 * @param {*} vettingType
 * @param {*} supplierId
 * @param {*} locationId
 * @param {*} manufacturingTypeId
 * @returns
 */
export async function getCapabilityAuditTrails(
  vettingType,
  supplierId,
  locationId,
  manufacturingTypeId,
) {
  const config = await appConfig()
  let url = `${config.mfelcm.rfiInternalAppServiceUrl}/${config.mfelcm.services.getCapabilityAuditTrails}`

  url = url
    .replace('VETTING_TYPE', (vettingType || '').toLowerCase())
    .replace('SUPPLIER_ID', supplierId)
    .replace('MANUFACTURING_TYPE_ID', manufacturingTypeId)

  if (locationId) {
    url = url + `&location_id=${locationId}`
  }

  return new Promise((resolve, reject) => {
    handleGet(url)
      .then((response) => resolve(response.data))
      .catch((error) => {
        logErrorApiCalls(error)
        reject(error)
      })
  })
}
